import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { func, shape, string } from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Skeleton, Slide, Slider, useTravelPurchaseContext } from 'components';
import { convertDateString, formatDate, formatLocalizedCurrency, getShortMonth } from 'helpers';
import { useSafeState } from 'hooks';

import 'assets/styles/components/travel-purchase/travel-selection.scss';

const TravelSelectionTable = ({ travelDepartureParams, setTravelDepartureParams }) => {
  const { t } = useTranslation();
  const { travelDeparture, travelDepartureLoading, travel, durationName, getTravelDeparture } =
    useTravelPurchaseContext();
  const { setValue, control, getValues } = useFormContext();
  const selectedMonthYear = useWatch({ control, name: 'selection.monthYear' });

  /**
   * State for the active date selected in the month-year slider
   * Saved as a date string in the format 'MM-yyyy'
   */
  const activeMonthYear = travelDepartureParams?.monthYear || selectedMonthYear?.value;
  const storedDepartureDate = useSelector((state) => state.travelPurchase.departureDate);
  const storedReturnDate = useSelector((state) => state.travelPurchase.returnDate);

  // State for the selected travel in the table
  const [selectedDate, setSelectedDate] = useSafeState(null);
  // Index of the active month in the slider
  const [startIndex, setStartIndex] = useSafeState(0);

  const travelPrice = useMemo(() => {
    return formatLocalizedCurrency(travel?.priceNotC2C, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      style: 'currency',
      currency: 'EUR'
    });
  }, [travel]);

  const travelMonthYear = useMemo(() => {
    return (
      travelDeparture?.periods?.map((period) => ({
        value: convertDateString(period.id),
        label: period.name
      })) || []
    );
  }, [travelDeparture]);

  /**
   * Visibles dates for the active month/year in the table
   */
  const availableTravelDates = useMemo(
    () => travelDeparture?.dates?.map((date) => ({ departureDate: date.name, returnDate: date.returnDate })) || [],
    [travelDeparture]
  );

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (storedDepartureDate || activeMonthYear) {
      /**
       * Set active month and slider start index depending on the selected departure period
       */
      const index = travelDeparture?.dates?.findIndex(
        (date) => date.name === activeMonthYear || date.name === storedDepartureDate
      );
      setStartIndex(index >= 0 ? index : 0);
    }
  }, [activeMonthYear, storedDepartureDate]);

  useEffect(() => {
    // Init form selectedTravel value with stored departure date in redux store
    if (storedDepartureDate && storedReturnDate) {
      setValue('selection.selectedTravel', { departureDate: storedDepartureDate, returnDate: storedReturnDate });
      setSelectedDate({ departureDate: storedDepartureDate, returnDate: storedReturnDate });
    }
  }, [storedDepartureDate, storedReturnDate]);

  // -----------------------------------------------------------------------------------------------------
  // Component methods
  // -----------------------------------------------------------------------------------------------------
  const onSelectDate = ({ departureDate, returnDate }) => {
    const newDates = selectedDate?.returnDate === returnDate ? null : { departureDate, returnDate };
    setSelectedDate(newDates);

    // Save value in the travelPurchaseForm
    setValue('selection.selectedTravel', newDates);
  };

  const handleMonthYearClick = (value) => {
    const formSelectionValues = getValues('selection');
    setTravelDepartureParams((prev) => ({ ...prev, monthYear: value }));

    // Call API to get the available dates depending on the selected month-year
    getTravelDeparture({
      from: formSelectionValues?.from?.value,
      duration: formSelectionValues?.duration?.value,
      ...travelDepartureParams,
      monthYear: value
    });
  };

  return (
    <>
      {travelDepartureLoading && <Skeleton classNames="w-full h-52" />}

      {!travelDepartureLoading && !isEmpty(travelDeparture) && (
        <div className="travel-selection">
          {/* Table Slider and Header */}
          <div className="bg-primary-150 lg:bg-transparent">
            <p className="text-center pt-2 lg:hidden">{formatDate(availableTravelDates?.[0]?.departureDate, 'yyyy')}</p>
            <Slider
              type="slide"
              options={{
                breakpoints: { 640: { perPage: 8 }, 1024: { perPage: 4 }, 1900: { perPage: 6 } },
                perPage: 4,
                mediaQuery: 'min',
                rewind: true,
                start: startIndex
              }}
            >
              {travelMonthYear.map(({ value, label }, i) => (
                <Slide key={`travel-month-${i}`}>
                  <button
                    type="button"
                    className={clsx('uppercase date-container', value === activeMonthYear && 'active')}
                    onClick={() => handleMonthYearClick(value)}
                  >
                    {/* Mobile - Month only */}
                    <span className="lg:hidden">{getShortMonth(value)}</span>

                    {/* Desktop - Month, Year and Price */}
                    <div className="hidden lg:h-full lg:w-full lg:flex-col-center-between lg:gap-2">
                      <div className="lg:font-normal">{label}</div>
                      <div className="font-normal normal-case text-secondary-500">
                        <span className="text-sm">{t('purchase:priceFromShort')}</span>
                        <span className="font-bold ml-1">{travelPrice}</span>
                        <span className="font-bold">{t('unitPrice')}</span>
                      </div>
                    </div>
                  </button>
                </Slide>
              ))}
            </Slider>
            <div className="bg-primary-200 p-2 border-b border-primary-500 lg:rounded-t-2xl">
              <div className="uppercase text-center lg:hidden">{formatDate(activeMonthYear, 'MMMM')}</div>

              <div className="hidden lg:grid lg:grid-cols-4">
                <div className="text-center">{t('purchase:departureDate')}</div>
                <div className="text-center">{t('purchase:duration')}</div>
                <div className="text-center">{t('purchase:backDateLabel')}</div>
                <div className="text-right pr-6">{t('purchase:unitTotalPrice')}</div>
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="travel-table">
            {availableTravelDates.length === 0 && <div className="text-center p-4">{t('purchase:noTravelDates')}</div>}

            {availableTravelDates.map(({ departureDate, returnDate }, i) => (
              <div
                key={`travel-line-${i}`}
                className={clsx('travel-table-line', departureDate === selectedDate?.departureDate && 'selected')}
                onClick={() => onSelectDate({ departureDate, returnDate })}
              >
                <div
                  className={clsx(
                    /* eslint-disable */
                    'date-block text-over bg-primary-150 p-2 flex-col-center-center gap-1 col-span-3 lg:flex-row lg:p-4',
                    /* eslint-enable */
                    i + 1 === availableTravelDates.length && 'lg:rounded-bl-2xl'
                  )}
                >
                  <span className="uppercase text-sm md:text-base">{formatDate(departureDate, 'EEE dd MMMM')}</span>
                </div>

                <div className="hidden text-over lg:block lg:col-span-3 lg:text-center">
                  {durationName({ short: false })}
                </div>
                <div className="hidden text-over lg:block lg:col-span-3 lg:text-center lg:uppercase">
                  {formatDate(returnDate, 'EEE dd MMMM')}
                </div>

                <div className="price-block">
                  <p className="lg:text-sm">{t('purchase:priceFrom')}</p>
                  <p className="font-bold">
                    {travelPrice}
                    {t('unitPrice')}
                  </p>
                </div>

                <div className="col-span-5 text-over text-right text-sm pr-1 md:text-base md:pr-6 lg:hidden">
                  <p>{durationName({ short: true })}</p>
                  {t('purchase:backDate', { date: formatDate(returnDate, 'EEE dd MMMM') })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

TravelSelectionTable.propTypes = {
  travelDepartureParams: shape({
    monthYear: string,
    from: string,
    duration: string
  }),
  setTravelDepartureParams: func.isRequired
};

export default TravelSelectionTable;
